/**
 * Erinnerungen: Fälligkeit und Nachfassen.
 *
 * Diese Datei ist der Grund, warum es `packages/shared` gibt. Die Regel „fällig um
 * 08:00, danach nach 15 und 45 Minuten nachfassen" wird an zwei Orten gebraucht —
 * in der App (was zeige ich als offen an?) und im Scheduler (wen benachrichtige ich
 * jetzt?). Zwei Kopien derselben Regel driften auseinander, und der Nutzer merkt es
 * daran, dass die App etwas anderes behauptet als sein Telefon.
 */

import { addDays, isoDateInZone, isoWeekdayInZone, wallTimeToUtc } from './time.js';
import type { IsoDate, WallTime } from './time.js';

/**
 * Abstände des Nachfassens in Minuten nach der Fälligkeit.
 * Stufe 0 = pünktlich, danach 15 und 45 Minuten.
 */
export const ESCALATION_OFFSETS_MIN = [0, 15, 45] as const;

/** Nach diesem Zeitpunkt (Ortszeit) wird nicht mehr genervt, sondern abgeschlossen. */
export const DAY_END_WALL_TIME: WallTime = '21:30';

export type ReminderStatus = 'pending' | 'acknowledged' | 'skipped' | 'expired';

export interface ReminderState {
  /** Geplante Fälligkeit (UTC). */
  dueAt: Date;
  /** Wie oft schon benachrichtigt wurde: 0 = noch gar nicht. */
  escalationStep: number;
  status: ReminderStatus;
}

/**
 * Wann muss der Scheduler diese Erinnerung das nächste Mal anfassen?
 * `null` heißt: nie wieder — quittiert, übersprungen, abgelaufen oder ausgereizt.
 */
export function nextFireAt(state: ReminderState): Date | null {
  if (state.status !== 'pending') return null;
  const offset = ESCALATION_OFFSETS_MIN[state.escalationStep];
  if (offset === undefined) return null;
  return new Date(state.dueAt.getTime() + offset * 60_000);
}

/** Ist die Erinnerung jetzt zu senden? */
export function isDue(state: ReminderState, now: Date): boolean {
  const fireAt = nextFireAt(state);
  return fireAt !== null && fireAt.getTime() <= now.getTime();
}

/** Sind alle Nachfass-Stufen ausgereizt? */
export function isExhausted(state: ReminderState): boolean {
  return state.escalationStep >= ESCALATION_OFFSETS_MIN.length;
}

/**
 * Der Tag ist vorbei — offene Erinnerungen laufen ab, statt am nächsten Morgen
 * verspätet aufzuschlagen. Eine Erinnerung von gestern ist kein Hinweis mehr,
 * sondern ein Vorwurf.
 */
export function isExpiredAtDayEnd(state: ReminderState, now: Date, timeZone: string): boolean {
  if (state.status !== 'pending') return false;
  const dayOfDue = isoDateInZone(state.dueAt, timeZone);
  const dayEnd = wallTimeToUtc(dayOfDue, DAY_END_WALL_TIME, timeZone);
  return now.getTime() > dayEnd.getTime();
}

// --- Zeitpläne ---------------------------------------------------------------

/**
 * Wann etwas fällig wird. Bewusst klein gehalten: mehr Ausdrucksstärke wäre
 * erfundene Anforderung. `months` deckt den Fall „Vitamin D3 von Oktober bis März"
 * ab, `weekdays` den Fall „nur an Trainingstagen" (dort später über Trainingsplan).
 */
export interface Schedule {
  /** Uhrzeiten in Ortszeit, an denen die Sache ansteht. */
  times: WallTime[];
  /** Wochentage 1–7 (Mo–So). Leer/weggelassen = jeden Tag. */
  weekdays?: number[];
  /** Monate 1–12, in denen die Regel gilt. Leer/weggelassen = ganzjährig. */
  months?: number[];
}

/** Gilt der Zeitplan an diesem Kalendertag? */
export function scheduleAppliesOn(schedule: Schedule, date: IsoDate, timeZone: string): boolean {
  const noonUtc = wallTimeToUtc(date, '12:00', timeZone);

  if (schedule.months && schedule.months.length > 0) {
    const month = Number(date.slice(5, 7));
    if (!schedule.months.includes(month)) return false;
  }

  if (schedule.weekdays && schedule.weekdays.length > 0) {
    const weekday = isoWeekdayInZone(noonUtc, timeZone);
    if (!schedule.weekdays.includes(weekday)) return false;
  }

  return true;
}

/** Alle Fälligkeiten eines Kalendertages, aufsteigend, als UTC-Zeitpunkte. */
export function dueTimesOn(schedule: Schedule, date: IsoDate, timeZone: string): Date[] {
  if (!scheduleAppliesOn(schedule, date, timeZone)) return [];
  return schedule.times
    .map((time) => wallTimeToUtc(date, time, timeZone))
    .sort((a, b) => a.getTime() - b.getTime());
}

/**
 * Fälligkeiten über einen Zeitraum — was der Scheduler vorausplant und die
 * Tagesansicht anzeigt.
 */
export function dueTimesBetween(
  schedule: Schedule,
  fromDate: IsoDate,
  toDate: IsoDate,
  timeZone: string,
): Date[] {
  const result: Date[] = [];
  let cursor = fromDate;
  // Schutz gegen Endlosschleife bei vertauschten Grenzen.
  for (let guard = 0; guard < 400 && cursor <= toDate; guard += 1) {
    result.push(...dueTimesOn(schedule, cursor, timeZone));
    cursor = addDays(cursor, 1);
  }
  return result;
}

/**
 * Ab wann sich eine Einnahme abhaken lässt: 15 Minuten vorher.
 *
 * Vorher steht sie mit Uhrzeit auf „Heute", aber ohne Knopf — ein „Genommen"
 * am Morgen für die Abend-Einnahme wäre ein Tippfehler, kein Protokoll
 * (Wunsch aus der Geräteabnahme 12.10.2026).
 */
export const ACK_LEAD_MIN = 15;

export function acknowledgeableFrom(dueAt: Date): Date {
  return new Date(dueAt.getTime() - ACK_LEAD_MIN * 60_000);
}

export function canAcknowledge(dueAt: Date, now: Date): boolean {
  return now.getTime() >= acknowledgeableFrom(dueAt).getTime();
}
